import { get_token } from "@/router";
import {
  get_profile_cover,
  get_profile_logo,
  normalize_organization_profile,
} from "@/utils/organization_profile";
export { update_company_profile_details } from "./organization_profile_setup_profile_api";

function get_uploaded_file_url(response) {
  return [
    response?.data?.file_url,
  ].find(Boolean) || null;
}

export function first_present(values) {
  return values.find((value) => value !== null && value !== undefined && value !== "") || "";
}

export function parse_json_or_null(value) {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function get_organization_type(type) {
  if (!type) return "";
  if (typeof type === "string") return type;

  return (
    type.type_of_organization || "");
}

function without_placeholder_fallback(value, fallbacks = []) {
  const text = String(value || "").trim();
  return fallbacks.includes(text) ? "" : text;
}

function html_to_plain_text(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  return parser_is_available()
    ? parse_html_text(text)
    : strip_html_tags(text);
}

function parser_is_available() {
  return typeof DOMParser !== "undefined";
}

function parse_html_text(text) {
  const doc = new DOMParser().parseFromString(text, "text/html");
  return (doc.body?.textContent || "").trim();
}

function strip_html_tags(text) {
  return text.replace(/<[^>]*>/g, "").trim();
}

function get_profile_setup_details(organization) {
  const details = {};
  details.organization_name = without_placeholder_fallback(
    first_present([organization.company_name, organization.profile_name]),
    ["Your Organization", "Your Organisation"]
  );
  details.type_of_organization = without_placeholder_fallback(
    get_organization_type(organization.type_of_organization),
    ["Organization", "Organisation"]
  );
  details.description = html_to_plain_text(organization.description);
  details.email = organization.email || "";
  details.tell_number = organization.tell_number || "";
  details.location = organization.location || "";
  return details;
}

function get_profile_setup_colors(
  organization,
  details,
  current_form,
  defaults,
  saved_branding
) {
  const colors = {};
  colors.primary_color = first_present([
    saved_branding?.primary_color,
    organization.primary_color,
    details.organization_primary_color,
    current_form.primary_color,
    defaults.primary_color,
  ]);
  colors.secondary_color = first_present([
    saved_branding?.secondary_color,
    organization.secondary_color,
    details.organization_secondary_color,
    current_form.secondary_color,
    defaults.secondary_color,
  ]);
  colors.tertiary_color = first_present([
    saved_branding?.tertiary_color,
    organization.tertiary_color,
    details.organization_tertiary_color,
    current_form.tertiary_color,
    defaults.tertiary_color,
  ]);
  return colors;
}

function logo_preview_field(has_logo_file, value) {
  return has_logo_file ? {} : { logo_preview: value || "" };
}

function cover_preview_field(has_cover_file, value) {
  return has_cover_file ? {} : { cover_preview: value || "" };
}

function get_profile_image_previews(company, details, has_logo_file, has_cover_file) {
  return Object.assign(
    {},
    logo_preview_field(has_logo_file, get_profile_logo(company, details)),
    cover_preview_field(has_cover_file, get_profile_cover(company, details))
  );
}

export function build_profile_setup_form_values({
  company,
  current_form,
  defaults,
  details,
  has_cover_file,
  has_logo_file,
  saved_branding,
}) {
  const company_source = company || {};
  const organization = normalize_organization_profile(
    company_source,
    details,
    saved_branding
  );

  const image_previews = get_profile_image_previews(
    company_source,
    details,
    has_logo_file,
    has_cover_file
  );

  const colors = get_profile_setup_colors(
    organization,
    details,
    current_form,
    defaults,
    saved_branding
  );

  return Object.assign(
    get_profile_setup_details(organization),
    colors,
    image_previews
  );
}

export function upload_company_profile_image({
  file,
  company_id,
  endpoint,
  missing_company_message,
  upload_error_message,
}) {
  return new Promise((resolve, reject) => {
    if (!company_id) {
      reject(new Error(missing_company_message));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", company_id);

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}${endpoint}`,
      type: "POST",
      headers: {
        Authorization: `Token ${get_token()}`,
      },
      data: formData,
      processData: false,
      contentType: false,
      success: (response) => resolve(get_uploaded_file_url(response)),
      error: (xhr) =>
        reject(new Error(xhr?.statusText || upload_error_message)),
    });
  });
}

export function delete_company_profile_image(company_id, file_type, error_message) {
  return new Promise((resolve, reject) => {
    if (!company_id) {
      resolve(false);
      return;
    }

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/delete_profile_image/`,
      method: "POST",
      data: JSON.stringify({
        company_id,
        file_type,
      }),
      headers: {
        Authorization: `Token ${get_token()}`,
        "Content-Type": "application/json",
      },
      success: () => resolve(true),
      error: (xhr) =>
        reject(new Error(xhr?.statusText || error_message)),
    });
  });
}

export function get_company_profile_data(response) {
  return (
    response?.data?.company_data ||
    response?.company_data ||
    response?.data ||
    null
  );
}

function set_css_variable(root, property, value) {
  if (value) root.style.setProperty(property, value);
}

export function apply_organization_branding(branding) {
  if (!branding) return;

  const root = document.documentElement;

  set_css_variable(root, "--primary-color", branding.primary_color);
  set_css_variable(root, "--accent-color", branding.primary_color);
  set_css_variable(root, "--secondary-color", branding.secondary_color);
  set_css_variable(root, "--tertiary-color", branding.tertiary_color);

  if (branding.logo_preview && !String(branding.logo_preview).startsWith("blob:")) {
    localStorage.setItem("organization_logo", branding.logo_preview);
  }
}
